import React from 'react';
import { SiteOffice } from '@/services/siteOfficeService';
import SiteOfficeCard from './SiteOfficeCard';
import CustomOfficeInput from './CustomOfficeInput';
import CustomSiteOfficeCard from './CustomSiteOfficeCard';
import { useSiteOfficeDataCheck } from '@/hooks/useSiteOfficeDataCheck';
import { useSiteOffice } from '@/contexts/SiteOfficeContext';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface SiteOfficeGridProps {
  siteOffices: SiteOffice[];
  selectedOffice: string;
  customOffice: string;
  showCustomInput: boolean;
  isCreatingCustom: boolean;
  onOfficeSelect: (office: string) => void;
  onCustomSelect: () => void;
  onCustomOfficeChange: (value: string) => void;
  onEditCustomOffice: (officeId: string, newName: string) => Promise<void>;
  onDeleteCustomOffice: (officeId: string) => Promise<void>;
}

const SiteOfficeGrid = ({
  siteOffices,
  selectedOffice,
  customOffice,
  showCustomInput,
  isCreatingCustom,
  onOfficeSelect,
  onCustomSelect,
  onCustomOfficeChange,
  onEditCustomOffice,
  onDeleteCustomOffice
}: SiteOfficeGridProps) => {
  // Check user's site office access
  const { canAccessAllSiteOffices, userSiteOfficeId } = useSiteOffice();
  
  // Use the new hook to check data availability for each site office
  const { hasData, dataStatus, loading: dataLoading } = useSiteOfficeDataCheck(siteOffices);

  // Function to get site office identifier for data filtering
  const getSiteOfficeIdentifier = (officeName: string) => {
    switch (officeName) {
      case 'Site Office Regional Timur':
        return 'SO-TIMUR';
      case 'Site Office Regional Barat':
        return 'SO-BARAT';
      case 'Site Office Regional Tengah':
        return 'SO-TENGAH';
      default:
        return officeName.toUpperCase().replace(/\s+/g, '-');
    }
  };

  // Function to check if office is custom (based on description or name pattern)
  const isCustomOffice = (office: SiteOffice) => {
    return office.description === 'Custom site office' || office.name.toLowerCase().includes('custom');
  };

  // Separate predefined and custom offices
  const predefinedOffices = siteOffices.filter(office => !isCustomOffice(office));
  const customOffices = siteOffices.filter(office => isCustomOffice(office));

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Access Restriction Notice */}
      {!canAccessAllSiteOffices && siteOffices.length === 1 && (
        <Alert className="border-blue-200 bg-blue-50">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            Akun Anda hanya memiliki akses ke <strong>{siteOffices[0]?.name}</strong>. 
            Hubungi administrator jika Anda memerlukan akses ke site office lain.
          </AlertDescription>
        </Alert>
      )}

      {/* Section Title */}
      <div className="text-center space-y-2">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
          {canAccessAllSiteOffices ? 'Select Your Site Office' : 'Site Office Anda'}
        </h3>
        <p className="text-sm sm:text-base text-muted-foreground max-w-lg mx-auto">
          {canAccessAllSiteOffices 
            ? 'Choose the regional site office that corresponds to your work location'
            : 'Site office yang dapat Anda akses berdasarkan penugasan akun'}
        </p>
        {dataLoading && (
          <p className="text-xs text-primary">Checking data availability...</p>
        )}
      </div>

      {/* Predefined Office Options */}
      <div className="space-y-4 sm:space-y-6">
        <div className="flex justify-center">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 max-w-4xl">
            {predefinedOffices.map((office, index) => (
              <div 
                key={office.id} 
                className="animate-slideInLeft"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <SiteOfficeCard
                  office={office}
                  isSelected={selectedOffice === office.name}
                  hasData={hasData(office.name)}
                  identifier={getSiteOfficeIdentifier(office.name)}
                  onSelect={onOfficeSelect}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Custom Offices Section */}
        {customOffices.length > 0 && (
          <div className="space-y-4">
            <div className="text-center">
              <h4 className="text-lg font-semibold text-gray-700">Custom Site Offices</h4>
              <p className="text-sm text-gray-500">
                {customOffices.length} custom office{customOffices.length > 1 ? 's' : ''} available
              </p>
            </div>
            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 max-w-4xl">
                {customOffices.map((office, index) => (
                  <div 
                    key={office.id} 
                    className="animate-slideInLeft"
                    style={{ animationDelay: `${(predefinedOffices.length + index) * 100}ms` }}
                  >
                    <CustomSiteOfficeCard
                      office={office}
                      isSelected={selectedOffice === office.name}
                      onSelect={onOfficeSelect}
                      onEdit={onEditCustomOffice}
                      onDelete={onDeleteCustomOffice}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Only show custom office creation for users with full access */}
        {canAccessAllSiteOffices && (
          <>
            {/* Divider */}
            <div className="flex items-center justify-center space-x-4 py-2 sm:py-4">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent max-w-sm"></div>
              <span className="text-xs sm:text-sm font-medium text-muted-foreground bg-background px-3 py-1.5 rounded-full border border-border whitespace-nowrap">
                Or Create New
              </span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent max-w-sm"></div>
            </div>

            {/* Custom Office Creation Option */}
            <div className="flex justify-center animate-fadeInUp">
              <div className="max-w-2xl w-full">
                <CustomOfficeInput
                  isSelected={selectedOffice === 'custom'}
                  customOffice={customOffice}
                  showInput={showCustomInput}
                  isCreating={isCreatingCustom}
                  onSelect={onCustomSelect}
                  onCustomOfficeChange={onCustomOfficeChange}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SiteOfficeGrid;
